// State model and CSS generation for the Theme Editor. Kept apart from the UI so the
// generator and the round-trip parser can be reasoned about on their own.
//
// The builder owns one block of CSS: everything it emits replaces brand.style_overrides on
// publish. Because a brand may already carry hand-written CSS, seedFromBrand() parses what it
// recognizes back into controls and preserves everything it does not in `advanced`, which is
// re-emitted verbatim. Publishing therefore never silently drops rules it did not author.

import {type IBrand} from '@verdocs/js-sdk';
import {type IBrandTheme} from './BrandThemes';

export interface IBuilderState {
  primary: string;
  secondary: string;
  font: string;
  dialogAccent: string | null;
  linkColor: string | null;
  checkboxColor: string;
  dialogRadius: number;
  signHeaderBg: string;
  signHeaderFg: string;
  btnBg: string;
  btnFg: string;
  btnRadius: number;
  fieldTint: string;
  fieldAlpha: number;
  fieldRadius: number;
  reqBorder: string;
  signer1: string;
  signer2: string;
  signer3: string;
  advanced: string;
}

export const BUILDER_SECTIONS: {id: string; label: string; keys: (keyof IBuilderState)[]}[] = [
  {id: 'general', label: 'General', keys: ['primary', 'secondary', 'font']},
  {id: 'disclosures', label: 'Disclosure dialog', keys: ['dialogAccent', 'linkColor', 'checkboxColor', 'dialogRadius']},
  {
    id: 'signing',
    label: 'Signing',
    keys: [
      'signHeaderBg',
      'signHeaderFg',
      'btnBg',
      'btnFg',
      'btnRadius',
      'fieldTint',
      'fieldAlpha',
      'fieldRadius',
      'reqBorder',
      'signer1',
      'signer2',
      'signer3',
    ],
  },
  {id: 'advanced', label: 'Advanced CSS', keys: ['advanced']},
];

export const BUILDER_FONTS = [
  {value: "'Inter', system-ui, sans-serif", label: 'Inter (default)'},
  {value: "Georgia, 'Times New Roman', serif", label: 'Georgia (serif)'},
  {value: 'system-ui, sans-serif', label: 'System'},
];

const DEFAULT_PRIMARY = '#654DCB';
const DEFAULT_SECONDARY = '#55BC81';

export const normalizeHex = (value: string): string | null => {
  let v = (value || '').trim();
  if (!/^#?[0-9a-fA-F]{3}$|^#?[0-9a-fA-F]{6}$/.test(v)) return null;
  if (v[0] !== '#') v = `#${v}`;
  if (v.length === 4) v = `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  return v.toUpperCase();
};

export const shade = (hex: string, pct: number): string => {
  const safe = normalizeHex(hex) || DEFAULT_PRIMARY;
  const n = parseInt(safe.slice(1), 16);
  const f = 1 + pct / 100;
  const clamp = (c: number) => Math.max(0, Math.min(255, Math.round(c * f)));
  const r = clamp((n >> 16) & 255);
  const g = clamp((n >> 8) & 255);
  const b = clamp(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
};

export const luminance = (hex: string): number => {
  const safe = normalizeHex(hex) || DEFAULT_PRIMARY;
  const n = parseInt(safe.slice(1), 16);
  return (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
};

const alphaHex = (pct: number): string => {
  const v = Math.round(Math.max(0, Math.min(100, pct)) * 2.55);
  return (v < 16 ? '0' : '') + v.toString(16).toUpperCase();
};

export const contrastOn = (hex: string): string => (luminance(hex) >= 0.55 ? '#111827' : '#FFFFFF');

const btnRadiusPx = (state: IBuilderState) => (state.btnRadius >= 25 ? '999px' : `${state.btnRadius}px`);

/**
 * The published CSS block. The previews render from this exact text.
 *
 * Variables go on :root. The SDK's own convention is `:root.verdocs-custom-theme`, a class the
 * signing page never adds, so a block scoped to `.verdocs-custom-theme` alone never reaches a
 * component: header and button rules (plain selectors) work while every variable silently does
 * nothing. The class stays in the selector list as an alias for pages that do add it. Every
 * variable named here is one the shipped components actually read, in the value shape they read
 * it (`--verdocs-required-field-border` is a bare color inside `1px solid var(...)`).
 */
export const generateCss = (state: IBuilderState): string => {
  const lines: string[] = [];
  lines.push(':root, .verdocs-custom-theme {');
  lines.push(`  --verdocs-primary-color: ${state.primary};`);
  lines.push(`  --verdocs-primary-color-hover: ${shade(state.primary, -14)};`);
  lines.push(`  --verdocs-primary-font: ${state.font};`);
  lines.push(`  --verdocs-button-radius: ${btnRadiusPx(state)};`);
  lines.push(`  --verdocs-dialog-radius: ${state.dialogRadius}px;`);
  lines.push(`  --verdocs-field-radius: ${state.fieldRadius}px;`);
  lines.push(`  --verdocs-field-background: ${state.fieldTint}${alphaHex(state.fieldAlpha)};`);
  lines.push(`  --verdocs-required-field-border: ${state.reqBorder};`);
  lines.push(`  --verdocs-checkbox-color: ${state.checkboxColor};`);
  lines.push(`  --verdocs-link-color: ${state.linkColor || state.primary};`);
  lines.push(`  --signer-1-color: ${state.signer1};`);
  lines.push(`  --signer-1-color-faded: ${state.signer1}7F;`);
  lines.push(`  --signer-2-color: ${state.signer2};`);
  lines.push(`  --signer-2-color-faded: ${state.signer2}BF;`);
  lines.push(`  --signer-3-color: ${state.signer3};`);
  lines.push(`  --signer-3-color-faded: ${state.signer3}BF;`);
  if (state.dialogAccent) lines.push(`  --verdocs-dialog-title-color: ${state.dialogAccent};`);
  lines.push('}');
  lines.push('');
  lines.push('#verdocs-sign-header {');
  lines.push(`  background-color: ${state.signHeaderBg};`);
  lines.push(`  color: ${state.signHeaderFg};`);
  lines.push('}');
  lines.push('');
  lines.push('verdocs-button button.standard {');
  lines.push(`  background-color: ${state.btnBg};`);
  lines.push('}');
  lines.push('');
  lines.push('verdocs-button button.standard .button-label {');
  lines.push(`  color: ${state.btnFg};`);
  lines.push('}');
  if (state.advanced.trim()) {
    lines.push('');
    lines.push('/* Advanced overrides (appended as-is) */');
    lines.push(state.advanced.trim());
  }
  return lines.join('\n');
};

export const applyTheme = (state: IBuilderState, theme: IBrandTheme): IBuilderState => ({
  ...state,
  primary: theme.primary,
  secondary: theme.btnBg,
  signHeaderBg: theme.headerBg,
  signHeaderFg: theme.headerFg,
  btnBg: theme.btnBg,
  btnFg: theme.btnFg,
  linkColor: theme.accent,
  checkboxColor: theme.accent,
  reqBorder: theme.accent,
  fieldTint: theme.accent,
  fieldAlpha: 10,
  signer1: theme.accent,
  signer2: '#FFFFFF',
  signer3: luminance(theme.headerBg) < 0.5 ? theme.headerBg : theme.primary,
});

// --- Round-trip parsing -------------------------------------------------------------------
// Split a stylesheet into top-level `selector { body }` blocks plus whatever sits between them
// (comments, at-rules). Nested braces are not expected in these blocks; anything the builder
// cannot claim is returned as leftover text.

interface IParsedBlock {
  selector: string;
  body: string;
  raw: string;
}

const parseBlocks = (css: string): {blocks: IParsedBlock[]; leftover: string} => {
  const blocks: IParsedBlock[] = [];
  let leftover = '';
  let index = 0;

  while (index < css.length) {
    const open = css.indexOf('{', index);
    if (open === -1) {
      leftover += css.slice(index);
      break;
    }
    const close = css.indexOf('}', open);
    if (close === -1) {
      leftover += css.slice(index);
      break;
    }
    const selector = css.slice(index, open).trim();
    // An at-rule (media query, keyframes) can nest; hand the whole thing back untouched.
    if (selector.startsWith('@')) {
      leftover += css.slice(index, close + 1);
      index = close + 1;
      continue;
    }
    blocks.push({selector, body: css.slice(open + 1, close), raw: css.slice(index, close + 1)});
    index = close + 1;
  }

  return {blocks, leftover};
};

const declarations = (body: string): Record<string, string> => {
  const out: Record<string, string> = {};
  body
    .split(';')
    .map((d) => d.trim())
    .filter(Boolean)
    .forEach((decl) => {
      const colon = decl.indexOf(':');
      if (colon === -1) return;
      out[decl.slice(0, colon).trim()] = decl.slice(colon + 1).trim();
    });
  return out;
};

const pxValue = (value: string | undefined, fallback: number): number => {
  if (!value) return fallback;
  if (value.trim() === '999px') return 25;
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
};

// A field tint is emitted as #RRGGBBAA. Split it back into color plus percent.
const splitTint = (value: string | undefined, fallbackColor: string, fallbackAlpha: number) => {
  const v = (value || '').trim();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) {
    return {color: v.slice(0, 7).toUpperCase(), alpha: Math.round((parseInt(v.slice(7), 16) / 255) * 100)};
  }
  return {color: normalizeHex(v) || fallbackColor, alpha: fallbackAlpha};
};

const borderColor = (value: string | undefined): string | null => {
  const match = (value || '').match(/#[0-9a-fA-F]{3,8}/);
  return match ? normalizeHex(match[0].slice(0, 7)) : null;
};

// The variables block may be scoped to :root, the SDK's theme class, or both.
const isThemeVarsSelector = (selector: string) =>
  selector
    .split(',')
    .map((s) => s.trim())
    .some((s) => s === ':root' || s === '.verdocs-custom-theme' || s === ':root.verdocs-custom-theme');

const KNOWN_VARS = new Set([
  '--verdocs-primary-color',
  '--verdocs-primary-color-hover',
  '--verdocs-secondary-color',
  '--verdocs-primary-font',
  '--verdocs-button-radius',
  '--verdocs-dialog-radius',
  '--verdocs-field-radius',
  '--verdocs-field-background',
  '--verdocs-required-field-border',
  '--verdocs-checkbox-color',
  '--verdocs-link-color',
  '--verdocs-dialog-header-bg',
  '--verdocs-dialog-title-color',
  '--signer-1-color',
  '--signer-1-color-faded',
  '--signer-2-color',
  '--signer-2-color-faded',
  '--signer-3-color',
  '--signer-3-color-faded',
]);

/**
 * Build builder state for a brand: brand-record fields first, then anything recoverable from an
 * existing style_overrides block, with unrecognized CSS preserved in `advanced`.
 */
export const seedFromBrand = (brand: IBrand): IBuilderState => {
  const primary = normalizeHex(brand.primary_color || '') || DEFAULT_PRIMARY;
  const secondary = normalizeHex(brand.secondary_color || '') || DEFAULT_SECONDARY;

  const state: IBuilderState = {
    primary,
    secondary,
    font: BUILDER_FONTS[0].value,
    dialogAccent: null,
    linkColor: null,
    checkboxColor: secondary,
    dialogRadius: 8,
    signHeaderBg: primary,
    signHeaderFg: contrastOn(primary),
    btnBg: secondary,
    btnFg: contrastOn(secondary),
    btnRadius: 5,
    fieldTint: secondary,
    fieldAlpha: 10,
    fieldRadius: 3,
    reqBorder: secondary,
    signer1: primary,
    signer2: '#FFFFFF',
    signer3: shade(primary, -30),
    advanced: '',
  };

  const existing = (brand.style_overrides || '').trim();
  if (!existing) return state;

  const {blocks, leftover} = parseBlocks(existing);
  const unclaimed: string[] = [];

  blocks.forEach((block) => {
    const decls = declarations(block.body);
    const selector = block.selector.replace(/\s+/g, ' ');

    if (isThemeVarsSelector(selector)) {
      state.primary = normalizeHex(decls['--verdocs-primary-color'] || '') || state.primary;
      state.secondary = normalizeHex(decls['--verdocs-secondary-color'] || '') || state.secondary;
      if (decls['--verdocs-primary-font']) state.font = decls['--verdocs-primary-font'];
      state.btnRadius = pxValue(decls['--verdocs-button-radius'], state.btnRadius);
      state.dialogRadius = pxValue(decls['--verdocs-dialog-radius'], state.dialogRadius);
      state.fieldRadius = pxValue(decls['--verdocs-field-radius'], state.fieldRadius);
      const tint = splitTint(decls['--verdocs-field-background'], state.fieldTint, state.fieldAlpha);
      state.fieldTint = tint.color;
      state.fieldAlpha = tint.alpha;
      state.reqBorder = borderColor(decls['--verdocs-required-field-border']) || state.reqBorder;
      state.checkboxColor = normalizeHex(decls['--verdocs-checkbox-color'] || '') || state.checkboxColor;
      state.linkColor = normalizeHex(decls['--verdocs-link-color'] || '') || state.linkColor;
      // dialog-header-bg is the name an earlier generator emitted; the SDK reads title-color.
      state.dialogAccent =
        normalizeHex(decls['--verdocs-dialog-title-color'] || '') || normalizeHex(decls['--verdocs-dialog-header-bg'] || '') || state.dialogAccent;
      state.signer1 = normalizeHex(decls['--signer-1-color'] || '') || state.signer1;
      state.signer2 = normalizeHex(decls['--signer-2-color'] || '') || state.signer2;
      state.signer3 = normalizeHex(decls['--signer-3-color'] || '') || state.signer3;

      // Custom properties we do not model are kept so publishing cannot drop them.
      const extras = Object.entries(decls).filter(([prop]) => !KNOWN_VARS.has(prop));
      if (extras.length > 0) {
        unclaimed.push(`:root, .verdocs-custom-theme {\n${extras.map(([k, v]) => `  ${k}: ${v};`).join('\n')}\n}`);
      }
      return;
    }

    if (selector === '#verdocs-sign-header') {
      state.signHeaderBg = normalizeHex(decls['background-color'] || decls['background'] || '') || state.signHeaderBg;
      state.signHeaderFg = normalizeHex(decls['color'] || '') || state.signHeaderFg;
      return;
    }

    if (selector === 'verdocs-button button.standard') {
      state.btnBg = normalizeHex(decls['background-color'] || decls['background'] || '') || state.btnBg;
      return;
    }

    if (selector === 'verdocs-button button.standard .button-label') {
      state.btnFg = normalizeHex(decls['color'] || '') || state.btnFg;
      return;
    }

    if (selector === 'verdocs-field-textbox') {
      state.fieldRadius = pxValue(decls['border-radius'], state.fieldRadius);
      return;
    }

    if (selector === 'verdocs-field-textbox.required.required' || selector === 'verdocs-field-signature.required.required') {
      state.reqBorder = borderColor(decls['border']) || state.reqBorder;
      return;
    }

    unclaimed.push(block.raw.trim());
  });

  const remainder = [...unclaimed, leftover.trim()].filter(Boolean).join('\n\n').trim();
  // Our own generated comment is noise on re-import; everything else is the user's.
  state.advanced = remainder.replace('/* Advanced overrides (appended as-is) */', '').trim();

  return state;
};
